/**
 * Per-row Radius / Tracking / GBP / Hours on a location Edit panel.
 * Phone/Email stay Primary-only (#1670 for per-location contact columns).
 */
import { useRef, useState } from 'react';

import { PartField } from './PartField';

export const HOURS_DAYS: ReadonlyArray<readonly [string, string]> = [
  ['mon', 'Mon'],
  ['tue', 'Tue'],
  ['wed', 'Wed'],
  ['thu', 'Thu'],
  ['fri', 'Fri'],
  ['sat', 'Sat'],
  ['sun', 'Sun'],
];

export type LocationDetailsPatch = {
  service_radius_miles?: number | null;
  tracking_phone?: string;
  gbp_url?: string;
  hours?: Record<string, string>;
};

function isHttpUrl(raw: string): boolean {
  const value = raw.trim();
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function LocationRowDetails({
  locationId,
  radius,
  trackingPhone,
  gbpUrl,
  hours,
  contactFallback,
  onPatch,
}: {
  locationId: string;
  radius?: number | null;
  trackingPhone?: string | null;
  gbpUrl?: string | null;
  hours?: Record<string, string> | null;
  contactFallback?: { phone: string; email: string };
  onPatch: (patch: LocationDetailsPatch) => void;
}) {
  const [gbpError, setGbpError] = useState(false);
  const storedHours = hours ?? {};
  const hoursRef = useRef<Record<string, string>>(storedHours);
  const seededIdRef = useRef(locationId);
  if (seededIdRef.current !== locationId) {
    hoursRef.current = storedHours;
    seededIdRef.current = locationId;
  }

  function commitRadius(raw: string) {
    const trimmed = raw.trim();
    const current = radius ?? null;
    if (trimmed === '') {
      if (current == null) return;
      onPatch({ service_radius_miles: null });
      return;
    }
    const n = Number(trimmed);
    if (!Number.isInteger(n)) return;
    if (n === current) return;
    onPatch({ service_radius_miles: n });
  }

  function commitHoursDay(dayKey: string, raw: string) {
    const trimmed = raw.trim();
    const next: Record<string, string> = { ...hoursRef.current };
    if (trimmed === '') delete next[dayKey];
    else next[dayKey] = trimmed;
    hoursRef.current = next;
    onPatch({ hours: next });
  }

  const contactBits = [contactFallback?.phone?.trim(), contactFallback?.email?.trim()].filter(
    Boolean,
  );

  return (
    <div data-testid={`bb-location-${locationId}-details`} className="mt-3 border-t border-border/70 pt-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <div data-testid={`bb-location-${locationId}-radius`} className="min-w-[220px] flex-[1_1_100%]">
          <PartField
            label="Service radius"
            hint="how far you'll travel for jobs from this address"
            suffix="miles"
            placeholder="e.g. 25"
            inputId={`bb-location-${locationId}-radius-input`}
            value={radius != null ? String(radius) : ''}
            onCommit={commitRadius}
          />
        </div>
        <div data-testid={`bb-location-${locationId}-tracking`} className="min-w-[140px]">
          <PartField
            label="Tracking"
            inputId={`bb-location-${locationId}-tracking-input`}
            value={trackingPhone ?? ''}
            onCommit={(v) => onPatch({ tracking_phone: v })}
          />
        </div>
        <div data-testid={`bb-location-${locationId}-gbp`} className="min-w-[180px] flex-1">
          <PartField
            label="GBP"
            inputId={`bb-location-${locationId}-gbp-input`}
            value={gbpUrl ?? ''}
            invalid={gbpError}
            onCommit={(v) => {
              const trimmed = v.trim();
              if (trimmed && !isHttpUrl(trimmed)) {
                setGbpError(true);
                return;
              }
              setGbpError(false);
              onPatch({ gbp_url: trimmed });
            }}
          />
          {gbpError && (
            <p data-testid={`bb-location-${locationId}-gbp-error`} className="mt-1 text-[11px] text-destructive">
              Enter a full http(s) URL — e.g. https://example.com
            </p>
          )}
        </div>
      </div>
      <div data-testid={`bb-location-${locationId}-hours`} className="mt-2">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Hours
        </p>
        <div className="grid grid-cols-7 gap-1.5">
          {HOURS_DAYS.map(([dayKey, dayLabel]) => (
            <div key={dayKey} data-testid={`bb-location-${locationId}-hours-${dayKey}`}>
              <div className="mb-1 text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {dayLabel}
              </div>
              <PartField
                label=""
                inputId={`bb-location-${locationId}-hours-${dayKey}-input`}
                value={storedHours[dayKey] ?? ''}
                onCommit={(v) => commitHoursDay(dayKey, v)}
              />
            </div>
          ))}
        </div>
      </div>
      {contactFallback && (
        <p
          data-testid={`bb-location-${locationId}-contact-fallback`}
          className="mt-2 text-[13px] text-muted-foreground"
        >
          Phone & email: uses Primary&apos;s
          {contactBits.length > 0 ? ` · ${contactBits.join(' · ')}` : ''}
        </p>
      )}
    </div>
  );
}
